/**
 * Single source of truth for relationship dropdowns shared between:
 *   - /relationships/:matchId               (MatchDetail.jsx)            -> display
 *   - /relationships/:matchId/edit          (RelationshipEditor.jsx)     -> edit form
 *   - /relationships/:matchId/edit-section  (SectionEditor.jsx)          -> per-section edit form
 *
 * Backend canonical source: /app/backend/models/bio_field_registry.py
 *   - their_relationship_status: single | dating | in_relationship | married |
 *                                divorced | its_complicated | unknown
 *   - relationship_type: 15 canonical values (see REL_TYPE_OPTIONS below).
 *
 * Any drift between these surfaces produces silently-orphaned select values
 * in the UI (e.g. "complicated" written by /edit-section was never recognised
 * by /edit which uses "its_complicated"). Keep this file as the single truth.
 */

// 8 canonical values — exact match to bio_field_registry.py enum_values.
export const THEIR_REL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "dating", label: "Dating" },
  { value: "in_relationship", label: "In Relationship" },
  { value: "engaged", label: "Engaged" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "its_complicated", label: "It's Complicated" },
  { value: "unknown", label: "Unknown" },
];

// 15 canonical relationship_type values (canonical block) + 2 legacy values
// preserved purely so old DB rows still render a sensible label in the view.
// Legacy values are *not* offered in edit dropdowns (see REL_TYPE_OPTIONS_EDIT).
export const REL_TYPE_OPTIONS = [
  { value: "monogamous", label: "Monogamous", descriptor: "Seeking one exclusive romantic partner" },
  { value: "open_relationship", label: "Open Relationship", descriptor: "Committed partner, open to external sexual connections" },
  { value: "polyamorous", label: "Polyamorous", descriptor: "Open to multiple romantic and/or sexual relationships" },
  { value: "solo_poly", label: "Solo Poly", descriptor: "Polyamorous, independent, no primary partner" },
  { value: "polyfidelity", label: "Polyfidelity", descriptor: "Closed poly group, no outside partners" },
  { value: "casual_dating", label: "Casual Dating", descriptor: "Light, non-committed connections" },
  { value: "friends_with_benefits", label: "Friends With Benefits", descriptor: "Friendship with sexual intimacy, low romance" },
  { value: "lovers", label: "Lovers", descriptor: "Intimate romantic/sexual bond, flexible structure" },
  { value: "swingers", label: "Swingers", descriptor: "Committed couple, sexual experiences with others" },
  { value: "relationship_anarchy", label: "Relationship Anarchy", descriptor: "No predefined rules or hierarchy; custom agreements" },
  { value: "enm", label: "Ethical Non-Monogamy (ENM)", descriptor: "Broad non-exclusive relationships with consent" },
  { value: "polygamy", label: "Polygamy", descriptor: "Umbrella term, multiple spouses" },
  { value: "polygynous", label: "Polygynous", descriptor: "One man with multiple exclusive partners" },
  { value: "polyandry", label: "Polyandry", descriptor: "One woman with multiple exclusive partners" },
  { value: "exploring", label: "Exploring / Not Sure Yet", descriptor: "Open to discussion, undecided structure" },
  // Legacy values — view only, intentionally NOT included in edit dropdowns.
  { value: "open", label: "Open", descriptor: "Open to external connections", legacy: true },
  { value: "casual", label: "Casual", descriptor: "Light, non-committed connections", legacy: true },
];

// Subset of REL_TYPE_OPTIONS that edit dropdowns should offer (canonical only).
export const REL_TYPE_OPTIONS_EDIT = REL_TYPE_OPTIONS.filter((o) => !o.legacy);

/**
 * `relationship_status` — the user's relationship to the persona (their
 * sister, partner, boss, etc.). 23 canonical values, each with a
 * description template using {match} / {user} placeholders. NOT in
 * bio_field_registry (NOT_BIO_TIERED_FIELDS — manual UI field). The
 * backend `SELECT_FIELD_OPTIONS["relationship_status"]` list in
 * routes/matches.py must match the `value`s here exactly.
 */
export const RELATIONSHIP_TYPES = [
  { value: "stranger", label: "Stranger", description: "{match} has never met {user} before." },
  { value: "acquaintance", label: "Acquaintance", description: "{match} has met {user} before but they hardly know each other." },
  { value: "friend", label: "Friend", description: "{match} and {user} are mutual friends only." },
  { value: "best_friend", label: "Best Friend", description: "{match} and {user} are the best of friends." },
  { value: "love_interest", label: "Love Interest", description: "{match} is a romantic interest of {user}." },
  { value: "partner", label: "Partner", description: "{match} and {user} are in a relationship." },
  { value: "life_partner", label: "Life-Partner", description: "{match} and {user} are committed life partners." },
  { value: "brother", label: "Brother", description: "{match} is {user}'s brother." },
  { value: "sister", label: "Sister", description: "{match} is {user}'s sister." },
  { value: "step_brother", label: "Step-Brother", description: "{match} is {user}'s step brother." },
  { value: "step_sister", label: "Step-Sister", description: "{match} is {user}'s step sister." },
  { value: "mother", label: "Mother", description: "{match} is {user}'s mother." },
  { value: "father", label: "Father", description: "{match} is {user}'s father." },
  { value: "grandmother", label: "Grand-Mother", description: "{match} is {user}'s grand mother." },
  { value: "grandfather", label: "Grand-Father", description: "{match} is {user}'s grand father." },
  { value: "daughter", label: "Daughter", description: "{match} is {user}'s daughter." },
  { value: "son", label: "Son", description: "{match} is {user}'s son." },
  { value: "cousin", label: "Cousin", description: "{match} is {user}'s cousin." },
  { value: "work_colleague", label: "Work Colleague", description: "{match} is {user}'s work colleague." },
  { value: "teacher", label: "Teacher", description: "{match} is one of {user}'s teachers." },
  { value: "boss", label: "Boss", description: "{match} is {user}'s work boss." },
  { value: "client", label: "Client", description: "{match} is one of {user}'s clients." },
  { value: "student", label: "Student", description: "{match} is one of {user}'s students." },
];

/**
 * `persona_connection` — 13-level stage scale (Spark to Union).
 * Same values rendered in /edit and in the onboarding wizard's stage cards.
 */
export const PERSONA_CONNECTION_LEVELS = [
  { value: 1, title: "Spark", description: "First awareness and curiosity, something catches your attention." },
  { value: 2, title: "Opening", description: "Initial contact begins; energy, tone, and intent are revealed." },
  { value: 3, title: "Resonance", description: "Conversation flows and mutual interest starts to feel natural." },
  { value: 4, title: "Safety", description: "Trust forms through consistency, respect, and emotional ease." },
  { value: 5, title: "Curiosity", description: "You begin wanting to know more about values, stories, inner worlds." },
  { value: 6, title: "Attraction", description: "Chemistry builds across emotional, mental, and physical levels." },
  { value: 7, title: "Vulnerability", description: "Walls soften; honesty and real feelings are shared." },
  { value: 8, title: "Alignment", description: "Values, desires, and intentions start to line up." },
  { value: 9, title: "Momentum", description: "Connection strengthens through shared time, effort, and intention." },
  { value: 10, title: "Intimacy", description: "Emotional and/or physical closeness deepens meaningfully." },
  { value: 11, title: "Integration", description: "You begin weaving each other into daily life and routines." },
  { value: 12, title: "Commitment", description: "Clear choice is made to prioritize and invest in the connection." },
  { value: 13, title: "Union", description: "A stable, mutual bond rooted in trust, love, and shared direction." },
];

/**
 * Display helper for a select field.
 * Returns the option's label when the stored value is recognised, otherwise
 * falls back to "underscore-replaced, title-cased" so unknown / legacy values
 * still render somewhat readably.
 */
export const findLabel = (options, value) => {
  if (value === undefined || value === null || value === "") return "";
  const match = options.find((o) => o.value === value);
  if (match) return match.label;
  // Fallback for orphan values not in the canonical list.
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
